import {
  TablePagination,
  useTheme,
  Button,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import React, { useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    justifyItems: 'flex-end',
    color: 'var(--color-text)',
    padding: '0 var(--space-s)',
    backgroundColor: 'var(--uishell-color-surface)',
    position: 'absolute',
    right: '1rem'
  },
  btn: {
    backgroundColor: '#141416',
    border: '1px solid #333339',
    boxShadow: '0 0 0 0 transparent',
    color: 'var(--accent-9-contrast)',
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: '#242428'
    },
    '&.Mui-disabled': {
      backgroundColor: '#242428',
      color: 'var(--color-text-muted)'
    }
  },
  metalMenuItem: {
    padding: 'var(--space-s) var(--space-m)',
    backgroundColor: '#141416',
    borderTop: '1px solid var(--color-border)',
    borderLeft: '1px solid var(--color-border)',
    borderRight: '1px solid var(--color-border)',
    '&:last-of-type': {
      borderBottom: '1px solid var(--color-border)'
    }
  },
  metalMenu: {
    '& .MuiPaper-root': {
      backgroundColor: '#242428',
      borderRadius: '0'
    },
    '& .MuiMenu-paper': {
      paddingTop: '0',
      paddingBottom: '0'
    },
    '& .MuiList-root': {
      padding: '0px'
    }
  }
}));

function TablePaginationActions(props) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange, disablePagination } = props;
  const [expandEllipses, setExpandEllipses] = useState(false);
  const ref = useRef(null);
  const buttonSize = 'small';
  const btnPixels = '40px';

  const handleFirstPageButtonClick = event => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = event => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = event => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = event => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const handleUpdatePage = (event, newPage) => {
    onPageChange(event, newPage);
  };

  const handleExpandEllipses = () => {
    setExpandEllipses(v => !v);
  };

  return Math.ceil(count / rowsPerPage) > 7 ? (
    <div className={classes.root}>
      <Button
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={handleBackButtonClick}
        disabled={page === 0 || disablePagination}
        aria-label="previous page"
        size={buttonSize}
        style={{
          borderRadius: 'var(--radius-l) 0 0 var(--radius-l)',
          textTransform: 'none'
        }}
      >
        <ArrowLeft size={17} />
        <p style={{ paddingLeft: '8px' }}>Previous</p>
      </Button>
      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={handleFirstPageButtonClick}
        disabled={page === 0 || disablePagination}
        aria-label="first page"
        size={buttonSize}
      >
        1
      </Button>
      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={e => handleUpdatePage(e, 1)}
        disabled={page === 1 || disablePagination}
        aria-label="second page"
        size={buttonSize}
      >
        2
      </Button>
      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={e => handleUpdatePage(e, 2)}
        disabled={page === 2 || disablePagination}
        aria-label="third page"
        size={buttonSize}
      >
        3
      </Button>

      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        ref={ref}
        onClick={() => setExpandEllipses(true)}
        disabled={disablePagination}
        aria-label="show more pages"
        size={buttonSize}
      >
        ...
      </Button>

      <Menu
        className={classes.metalMenu}
        style={{ maxHeight: '500px' }}
        variant="contained"
        color="primary"
        onClose={handleExpandEllipses}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom'
        }}
        keepMounted
        open={expandEllipses}
      >
        {Array.from({ length: Math.ceil(count / rowsPerPage) }).map(
          (_, index) =>
            index !== 0 &&
            index !== 1 &&
            index !== 2 &&
            index !== Math.ceil(count / rowsPerPage) - 3 &&
            index !== Math.ceil(count / rowsPerPage) - 2 &&
            index !== Math.ceil(count / rowsPerPage) - 1 &&
            index !== Math.ceil(count / rowsPerPage) ? (
              <MenuItem
                className={classes.metalMenuItem}
                sx={{ justifyContent: 'center' }}
                key={index}
                disabled={page === index}
                onClick={e => {
                  if (page !== index && !disablePagination) {
                    handleUpdatePage(e, index);
                    setExpandEllipses(false);
                  }
                }}
                aria-label={`${index + 1} page`}
              >
                <Typography sx={{}}>{index + 1}</Typography>
              </MenuItem>
            ) : null
        )}
      </Menu>

      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={e => handleUpdatePage(e, Math.ceil(count / rowsPerPage) - 3)}
        disabled={
          page === Math.ceil(count / rowsPerPage) - 3 || disablePagination
        }
        aria-label="first page"
        size={buttonSize}
      >
        {Math.ceil(count / rowsPerPage) - 2}
      </Button>
      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={e => handleUpdatePage(e, Math.ceil(count / rowsPerPage) - 2)}
        disabled={
          page === Math.ceil(count / rowsPerPage) - 2 || disablePagination
        }
        aria-label={`${Math.ceil(count / rowsPerPage) - 1} page`}
        size={buttonSize}
      >
        {Math.ceil(count / rowsPerPage) - 1}
      </Button>
      <Button
        style={{
          borderRadius: '0',
          borderLeft: '0px solid transparent',
          borderRight: '1px solid #333339',
          minWidth: btnPixels,
          width: btnPixels,
          height: btnPixels,
          minHeight: btnPixels,
          aspectRatio: '1:1'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={e => handleUpdatePage(e, Math.ceil(count / rowsPerPage) - 1)}
        disabled={
          page === Math.ceil(count / rowsPerPage) - 1 || disablePagination
        }
        aria-label={`${Math.ceil(count / rowsPerPage)} page`}
        size={buttonSize}
      >
        {Math.ceil(count / rowsPerPage)}
      </Button>
      <Button
        style={{
          borderRadius: '0 var(--radius-l) var(--radius-l) 0',
          borderLeft: '0px solid transparent',
          textTransform: 'none'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={handleNextButtonClick}
        disabled={
          page === Math.ceil(count / rowsPerPage) - 1 || disablePagination
        }
        aria-label="next page"
        size={buttonSize}
      >
        <p style={{ paddingRight: '8px' }}>Next</p>
        <ArrowRight size={17} />
      </Button>
    </div>
  ) : (
    <div className={classes.root}>
      <Button
        style={{
          borderRadius: 'var(--radius-l) 0 0 var(--radius-l)',
          textTransform: 'none'
        }}
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={handleBackButtonClick}
        disabled={page === 0 || disablePagination}
        aria-label="previous page"
        size={buttonSize}
      >
        <ArrowLeft size={17} />
        <p style={{ paddingLeft: '8px' }}>Previous</p>
      </Button>
      {Array.from({ length: Math.ceil(count / rowsPerPage) }).map(
        (_, index) => (
          <Button
            style={{
              borderRadius: '0',
              borderLeft: '0px solid transparent',
              borderRight: '1px solid #333339',
              minWidth: btnPixels,
              width: btnPixels,
              height: btnPixels,
              minHeight: btnPixels,
              aspectRatio: '1:1'
            }}
            className={classes.btn}
            variant="contained"
            color="primary"
            key={index}
            onClick={e => handleUpdatePage(e, index)}
            disabled={page === index || disablePagination}
            aria-label={`page ${index + 1}`}
            size={buttonSize}
          >
            <span style={{ margin: '0' }}>{index + 1}</span>
          </Button>
        )
      )}
      <Button
        className={classes.btn}
        variant="contained"
        color="primary"
        onClick={handleNextButtonClick}
        disabled={
          page >= Math.ceil(count / rowsPerPage) - 1 || disablePagination
        }
        aria-label="next page"
        size={buttonSize}
        style={{
          borderRadius: '0 var(--radius-l) var(--radius-l) 0',
          borderLeft: '0px solid transparent',
          textTransform: 'none'
        }}
      >
        <p style={{ paddingRight: '8px' }}>Next</p>
        <ArrowRight size={17} />
      </Button>
    </div>
  );
}

const TablePaginationComp = props => {
  const ActionsComponentDisabled = () => (
    <TablePaginationActions disablePagination={true} />
  );

  return (
    <TablePagination
      sx={{
        backgroundColor: 'var(--uishell-color-surface)',
        borderBottom: '0',
        '& .MuiToolbar-root': {
          padding: 'var(--space-m) 1rem',
          position: 'relative',
          borderTop: '1px solid #333339',
          '& .MuiTablePagination-spacer': {
            display: 'none'
          }
        },
        '& .MuiSelect-icon': {
          top: '2px',
          paddingLeft: '3px'
        }
      }}
      rowsPerPageOptions={[10, 20, 100, { label: 'All', value: -1 }]}
      count={props.count ? props.count : props.filteredRows.length}
      rowsPerPage={props.rowsPerPage}
      page={props.page}
      slotProps={{
        inputProps: { 'aria-label': 'rows per page' },
        native: true,
        disabled: props.disablePagination,
        select: {
          defaultValue: props.rowsPerPage,
          IconComponent: ChevronDown,
          MenuProps: {
            PaperProps: {
              sx: {
                backgroundColor: '#141416',
                padding: '0',
                borderRadius: 'var(--radius-l)',
                border: '1px solid var(--color-border)',

                '& .MuiList-root': {
                  padding: '0'
                },

                '& .MuiTablePagination-menuItem.Mui-selected, & .MuiTablePagination-menuItem.Mui-selected:hover': {
                  backgroundColor: '#242428 !important',
                  color: 'var(--color-text-muted)',
                  pointerEvents: 'none',
                  cursor: 'default',
                },

                '& .MuiMenuItem-root': {
                  display: 'flex',
                  justifyContent: 'center',
                  borderBottom: '1px solid var(--color-border)',
                  '&:last-of-type': {
                    borderBottom: '0'
                  }
                }
              }
            }
          }
        }
      }}
      onPageChange={props.handleChangePage}
      onRowsPerPageChange={props.handleChangeRowsPerPage}
      ActionsComponent={
        props.disablePagination
          ? ActionsComponentDisabled
          : TablePaginationActions
      }
    />
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

export default TablePaginationComp;
